import PropTypes from 'prop-types';
import Media from 'react-media';
import { Bed, Path, ShoppingBag, Camera } from '@phosphor-icons/react';

import { date } from '@eti/utils';
import { CarrierLogo, DangerousHtmlContent } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { BrandedFareInfo, RegionNew } from '@eti/types';
import { Inline, Stack, Heading } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { useBrandedFareName } from '../../../common/custom-hooks/useBrandedFareName';
import {
  ONE_DAY_STOPOVER,
  ONE_NIGHT_STOPOVER,
  SHORT_STOPOVER,
  TWO_NIGHTS_STOPOVER,
} from '../../../constants/eventSegmentTypes';
import { eventSegmentTitlesKeys } from '../../../utils/format';
import LocationAndTime from './TripDetailsLocationTime';
import TripDetailsDuration from './TripDetailsDuration';
import TripDetailsSegmentInfo from './TripDetailsSegmentInfo';
import {
  baggageColumnWrapperClass,
  CarrierColumn,
  CarrierName,
  FullWidthColumn,
  PlaceholderColumn,
  Segment,
  TripDetailsSegmentInfoWrapper,
} from './styles/tripDetailsSegment';
import TripDetailsBaggageIncluded from './TripDetailsBaggageIncluded';
import TripDetailsBaggageNotIncluded from './TripDetailsBaggageNotIncluded';
import TripDetailsSegmentLabel from './TripDetailsSegmentLabel';
import TripDetailsDifferentAirport from './TripDetailsDifferentAirport';

const headingStyles = css`
  color: #0057a7;
`;

const stopoverSegmentIcon = (type) => {
  switch (type) {
    case SHORT_STOPOVER:
      return <ShoppingBag color="#0057a7" size={18} weight="fill" />;

    case ONE_DAY_STOPOVER:
      return <Camera color="#0057a7" size={18} weight="fill" />;

    case ONE_NIGHT_STOPOVER:
    case TWO_NIGHTS_STOPOVER:
      return <Bed color="#0057a7" size={18} weight="fill" />;

    default:
      return <Path size={18} weight="regular" />;
  }
};

const TripDetailsSegment = ({
  arrivedAt,
  boundIncludedCheckedBaggage,
  brandedFareInformation,
  cabinClassName,
  departuredAt,
  destination = {},
  duration,
  equipmentCode,
  flightNumber,
  includesTrainSegments,
  isDifferentDestination,
  isDifferentOrigin,
  isExpanded,
  marketingCarrier = {},
  numberOfTechnicalStops,
  operatingCarrier = {},
  operatingInformation,
  origin = {},
  segmentIncludedCheckedBaggage,
  stopoverType,
  stops,
}) => {
  const { t } = useTranslation();
  const eventSegmentTitles = Object.fromEntries(
    Object.entries(eventSegmentTitlesKeys).map(([key, value]) => [key, t(value)]),
  );
  const brandedFareInfoAndMarketingCarrier = { brandedFareInformation, marketingCarrier };
  const brandedFareName = useBrandedFareName(brandedFareInfoAndMarketingCarrier);

  return (
    <>
      <TripDetailsSegmentLabel
        brandedFareName={brandedFareName}
        cabinClassName={cabinClassName}
        equipmentCode={equipmentCode}
        includesTrainSegments={includesTrainSegments}
        isExpanded={isExpanded}
      />
      <Segment data-testid="tripDetails-segment">
        <div className={FullWidthColumn}>
          <CarrierColumn>
            <CarrierLogo
              id={marketingCarrier.code || ''}
              imageAlt={marketingCarrier.name}
              logo={marketingCarrier.logo}
            />
            <Media
              query={mediaQueries.medium.up}
              render={() => (
                <TripDetailsSegmentInfoWrapper>
                  <CarrierName>{marketingCarrier.name}</CarrierName>
                  {isExpanded && (
                    <TripDetailsSegmentInfo
                      data-testid="tripDetails-segment-TripDetailsSegmentInfo"
                      flight={flightNumber}
                      marketingCarrier={marketingCarrier}
                      operatingCarrier={operatingCarrier}
                      operatingInformation={operatingInformation}
                    />
                  )}
                </TripDetailsSegmentInfoWrapper>
              )}
            />
          </CarrierColumn>
          <LocationAndTime
            airportCode={origin.code}
            airportName={origin.name}
            city={origin.cityName}
            dataTestid="trip-segment-origin-time"
            time={departuredAt}
          />
          <Stack>
            <TripDetailsDuration
              duration={duration}
              isExpanded={isExpanded}
              numberOfTechnicalStops={numberOfTechnicalStops}
              stops={stops}
            />
            {stopoverType && (
              <Inline alignY="center" noWrap spacing={8}>
                {stopoverSegmentIcon(stopoverType)}
                <Heading
                  className={headingStyles}
                  data-testid={`trip-segment-stopover-type-${stopoverType}`}
                  level={4}
                >
                  <DangerousHtmlContent content={eventSegmentTitles[stopoverType]} />
                </Heading>
              </Inline>
            )}
          </Stack>
          <LocationAndTime
            airportCode={destination.code}
            airportName={destination.name}
            city={destination.cityName}
            dataTestid="trip-segment-destination-time"
            durationInDays={date.durationInDays(new Date(departuredAt), new Date(arrivedAt))}
            time={arrivedAt}
          />
          <Media query={mediaQueries.small.only}>
            {(matches) => (matches ? <PlaceholderColumn /> : '')}
          </Media>
        </div>
        <div className={baggageColumnWrapperClass}>
          {boundIncludedCheckedBaggage || segmentIncludedCheckedBaggage ? (
            <TripDetailsBaggageIncluded
              boundIncludedCheckedBaggage={boundIncludedCheckedBaggage}
              segmentIncludedCheckedBaggage={segmentIncludedCheckedBaggage}
            />
          ) : (
            <TripDetailsBaggageNotIncluded />
          )}
        </div>
        <Media query={mediaQueries.small.only}>
          {(matches) =>
            isExpanded && matches ? (
              <TripDetailsSegmentInfoWrapper>
                <CarrierName>{marketingCarrier.name}</CarrierName>
                <TripDetailsSegmentInfo
                  data-testid="tripDetails-segment-TripDetailsSegmentInfo"
                  flight={flightNumber}
                  marketingCarrier={marketingCarrier}
                  operatingCarrier={operatingCarrier}
                  operatingInformation={operatingInformation}
                />
              </TripDetailsSegmentInfoWrapper>
            ) : (
              ''
            )
          }
        </Media>
      </Segment>
      {isExpanded && (isDifferentOrigin || isDifferentDestination) && (
        <TripDetailsDifferentAirport isExpanded={isExpanded} />
      )}
    </>
  );
};

const includedCheckdBaggageProTypes = PropTypes.shape({
  pieces: PropTypes.number,
  weight: PropTypes.number,
  weightUnit: PropTypes.string,
});

TripDetailsSegment.propTypes = {
  arrivedAt: PropTypes.string,
  boundIncludedCheckedBaggage: includedCheckdBaggageProTypes,
  brandedFareInformation: BrandedFareInfo,
  cabinClassName: PropTypes.string,
  departuredAt: PropTypes.string,
  destination: RegionNew,
  duration: PropTypes.number,
  equipmentCode: PropTypes.string,
  flightNumber: PropTypes.string,
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  includesTrainSegments: PropTypes.oneOf(['ALL', 'SOME', 'NONE']),
  isDifferentDestination: PropTypes.bool,
  isDifferentOrigin: PropTypes.bool,
  isExpanded: PropTypes.bool,
  marketingCarrier: PropTypes.shape({
    code: PropTypes.string,
    logo: PropTypes.shape({
      large: PropTypes.string,
      small: PropTypes.string,
    }),
    name: PropTypes.string,
  }),
  numberOfTechnicalStops: PropTypes.number,
  operatingCarrier: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
  }),
  operatingInformation: PropTypes.string,
  origin: RegionNew,
  segmentIncludedCheckedBaggage: includedCheckdBaggageProTypes,
  stopoverType: PropTypes.string,
  stops: PropTypes.number,
};

export default TripDetailsSegment;
